import React, { useState } from 'react';
import { ArrowLeft, CalendarClock, Clock3, Pencil, Trash2 } from 'lucide-react';
import { TripResponse } from '../lib/api';
import { formatDateShort, getDirectionLabel, getRouteName, normalizeTime } from '../lib/tripPresentation';

interface PlannedTripDetailPageProps {
  trip: TripResponse;
  onBack: () => void;
  onEditTrip: (trip: TripResponse) => void;
  onDeleteTrip: (tripId: string) => Promise<boolean>;
}

export const PlannedTripDetailPage: React.FC<PlannedTripDetailPageProps> = ({
  trip,
  onBack,
  onEditTrip,
  onDeleteTrip,
}) => {
  const [deleting, setDeleting] = useState(false);
  const hasTrafficProjection = Boolean(trip.trafficLevel && trip.trafficLevel !== 'UNKNOWN');

  const handleDelete = async () => {
    if (!window.confirm('¿Eliminar este viaje planificado?')) return;
    setDeleting(true);
    try {
      const removed = await onDeleteTrip(trip.id);
      if (removed) onBack();
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-6 pb-16 animate-fade-in">
      <div className="mb-6 flex items-center gap-4">
        <button
          onClick={onBack}
          className="inline-flex items-center gap-1.5 text-sm font-semibold text-gray-500 hover:text-gray-800 transition-colors"
        >
          <ArrowLeft size={16} />
          Volver
        </button>
        <div>
          <div className="flex items-center gap-2">
            <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-bold ${trip.routeId === '78' ? 'bg-orange-100 text-orange-700' : 'bg-blue-100 text-blue-700'}`}>
              R{trip.routeId}
            </span>
            <h2 className="text-2xl font-black text-gray-900">{getDirectionLabel(trip.direction)}</h2>
          </div>
          <p className="text-sm text-gray-500 mt-0.5">{formatDateShort(trip.travelDate)}</p>
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-3xl p-6 md:p-8 shadow-sm">
        <div className="divide-y divide-gray-100 text-sm">
          <div className="flex items-center justify-between py-3 first:pt-0">
            <span className="text-gray-500">Ruta</span>
            <span className="font-semibold text-gray-900">{getRouteName(trip.routeId)}</span>
          </div>
          <div className="flex items-center justify-between py-3">
            <span className="text-gray-500">Sentido</span>
            <span className="font-semibold text-gray-900">{getDirectionLabel(trip.direction)}</span>
          </div>
          <div className="flex items-center justify-between py-3">
            <span className="text-gray-500 inline-flex items-center gap-2">
              <CalendarClock size={15} />
              Fecha
            </span>
            <span className="font-semibold text-gray-900">{formatDateShort(trip.travelDate)}</span>
          </div>
          <div className="flex items-center justify-between py-3 last:pb-0">
            <span className="text-gray-500 inline-flex items-center gap-2">
              <Clock3 size={15} />
              Hora ingreso
            </span>
            <span className="font-semibold text-gray-900">{normalizeTime(trip.scheduledTime)}h</span>
          </div>
          {hasTrafficProjection && (
            <div className="flex items-center justify-between py-3 last:pb-0">
              <span className="text-gray-500">Tráfico proyectado</span>
              <span className="font-semibold text-gray-900">{trip.trafficLevel}</span>
            </div>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-4 mt-7 pt-6 border-t border-gray-100">
          <button
            onClick={() => onEditTrip(trip)}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl border border-blue-600 text-blue-600 font-semibold hover:bg-blue-50 text-sm transition-colors"
          >
            <Pencil size={15} />
            Editar viaje
          </button>
          <button
            onClick={() => void handleDelete()}
            disabled={deleting}
            className="inline-flex items-center gap-2 text-sm font-semibold text-red-500 hover:text-red-700 disabled:opacity-50 transition-colors"
          >
            <Trash2 size={15} />
            {deleting ? 'Eliminando...' : 'Eliminar viaje'}
          </button>
        </div>
      </div>
    </div>
  );
};
